import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { enableEntranceAnimations } from './utils/entranceAnimations';
import { AuthProvider } from './components/auth/AuthProvider';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/auth/PrivateRoute';
import Footer from './components/Footer';
import InstallPrompt from './components/InstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import CookieConsent from './components/CookieConsent';
import UnverifiedEmailBanner from './components/UnverifiedEmailBanner';
import useMigrateTestHistory from './hooks/useMigrateTestHistory';
import { cleanAuthStorage } from './services/api/config';

const Home = lazy(() => import('./pages/Home'));
const Test = lazy(() => import('./pages/Test'));
const Results = lazy(() => import('./pages/Results'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Profile = lazy(() => import('./pages/Profile'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ModuleManagement = lazy(() => import('./pages/admin/EnhancedModuleManagement'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const EmailManagement = lazy(() => import('./pages/admin/EmailManagement'));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary'));

// New Learning Module pages
const LearningModules = lazy(() => import('./pages/LearningModules'));
const ModuleDetail = lazy(() => import('./pages/ModuleDetail'));
// Expert pages
const ExpertBrowse = lazy(() => import('./pages/ExpertBrowse'));
const ExpertProfile = lazy(() => import('./pages/ExpertProfile'));
const BecomeExpert = lazy(() => import('./pages/BecomeExpert'));
const ExpertDashboard = lazy(() => import('./pages/expert/ExpertDashboard'));
const ExpertModules = lazy(() => import('./pages/expert/ExpertModules'));
const ExpertSubscription = lazy(() => import('./pages/expert/ExpertSubscription'));
const ExpertProfileEdit = lazy(() => import('./pages/expert/ExpertProfileEdit'));
const ModuleCreationWizard = lazy(() => import('./pages/expert/ModuleCreationWizard'));
const ForContributors = lazy(() => import('./pages/ForContributors'));
const TypePage = lazy(() => import('./pages/TypePage'));
const EnneagramIndex = lazy(() => import('./pages/EnneagramIndex'));
const WingPage = lazy(() => import('./pages/WingPage'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Inbox = lazy(() => import('./pages/Inbox'));
const AudienceMember = lazy(() => import('./pages/expert/AudienceMember'));

// Entrance animations are suppressed while hydrating prerendered HTML (see
// utils/entranceAnimations). Re-enable them once the user navigates client-side.
const EnableAnimationsOnNavigate = () => {
  const location = useLocation();
  const initialPathname = useRef(location.pathname);

  useEffect(() => {
    // Compare against the initial pathname instead of counting effect runs —
    // StrictMode double-invokes effects, which would miscount.
    if (location.pathname !== initialPathname.current) {
      enableEntranceAnimations();
    }
  }, [location.pathname]);

  return null;
};

const AppContent = () => {
  const [initError, setInitError] = React.useState<Error | null>(null);

  // Use the migration hook
  useMigrateTestHistory();

  // Clean up malformed auth data on app load with comprehensive error handling
  useEffect(() => {
    try {
      cleanAuthStorage();
    } catch (error) {
      console.error('Error cleaning auth storage:', error);
      setInitError(error instanceof Error ? error : new Error('Initialization failed'));
    }
  }, []);

  // Show error if initialization failed
  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Initialization Error</h1>
          <p className="text-gray-600 mb-4">
            There was a problem starting the application. This may be due to browser storage restrictions.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Try clearing your browser data or using a different browser.
          </p>
          <button
            onClick={() => {
              try {
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              } catch (e) {
                alert('Please manually clear your browser data and refresh the page.');
              }
            }}
            className="btn-primary"
          >
            Clear Data & Reload
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <UnverifiedEmailBanner />
      <main className="flex-grow">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/test" element={<Test />} />
            <Route path="/results" element={<Results />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Learning Module Routes */}
            <Route path="/learning" element={<LearningModules />} />
            <Route path="/module/:moduleId" element={<ModuleDetail />} />

            {/* Enneagram SEO Pages */}
            <Route path="/enneagram" element={<EnneagramIndex />} />
            <Route path="/enneagram/type/:typeId" element={<TypePage />} />
            <Route path="/enneagram/type/:typeId/wing/:wingId" element={<WingPage />} />

            {/* Expert Public Routes */}
            <Route path="/experts" element={<ExpertBrowse />} />
            <Route path="/experts/:expertId" element={<ExpertProfile />} />
            <Route path="/become-expert" element={<BecomeExpert />} />
            <Route path="/for-contributors" element={<ForContributors />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Inbox */}
            <Route
              path="/inbox"
              element={
                <PrivateRoute>
                  <Inbox />
                </PrivateRoute>
              }
            />

            {/* Expert Dashboard Routes */}
            <Route
              path="/expert/dashboard"
              element={
                <PrivateRoute expertOnly={true}>
                  <ExpertDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/expert/modules"
              element={
                <PrivateRoute expertOnly={true}>
                  <ExpertModules />
                </PrivateRoute>
              }
            />
            <Route
              path="/expert/modules/new"
              element={
                <PrivateRoute expertOnly={true}>
                  <ModuleCreationWizard />
                </PrivateRoute>
              }
            />
            <Route
              path="/expert/modules/:moduleId/edit"
              element={
                <PrivateRoute expertOnly={true}>
                  <ModuleManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/expert/profile"
              element={
                <PrivateRoute expertOnly={true}>
                  <ExpertProfileEdit />
                </PrivateRoute>
              }
            />
            <Route
              path="/expert/subscription"
              element={
                <PrivateRoute expertOnly={true}>
                  <ExpertSubscription />
                </PrivateRoute>
              }
            />
            <Route
              path="/expert/audience/:userId"
              element={
                <PrivateRoute expertOnly={true}>
                  <AudienceMember />
                </PrivateRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <PrivateRoute adminOnly={true}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route 
              path="/admin/tests" 
              element={
                <PrivateRoute adminOnly={true}>
                  <AdminDashboard />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/modules" 
              element={
                <PrivateRoute adminOnly={true}>
                  <ModuleManagement />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <PrivateRoute adminOnly={true}>
                  <UserManagement />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/media" 
              element={
                <PrivateRoute adminOnly={true}>
                  <MediaLibrary />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/analytics" 
              element={
                <PrivateRoute adminOnly={true}>
                  <Analytics />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <PrivateRoute adminOnly={true}>
                  <Settings />
                </PrivateRoute>
              } 
            />
            <Route 
              path="/admin/emails" 
              element={
                <PrivateRoute adminOnly={true}>
                  <EmailManagement />
                </PrivateRoute>
              } 
            />

          </Routes>
        </Suspense>
      </main>
      <Footer />
      <InstallPrompt />
      <OfflineIndicator />
      <CookieConsent />
    </div>
  );
};

// Everything inside the router — shared between the client app (BrowserRouter)
// and the build-time prerenderer (StaticRouter in entry-server.tsx).
export const AppInner = () => {
  return (
    <AuthProvider>
      <EnableAnimationsOnNavigate />
      <AppContent />
    </AuthProvider>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      {/* v7_startTransition wraps router updates in startTransition so they
          don't force prerendered Suspense content to client-render during
          hydration (React error #421). */}
      <Router future={{ v7_startTransition: true }}>
        <AppInner />
      </Router>
    </ErrorBoundary>
  );
};

export default App;